<script setup lang="ts">
import {
    useStrapiBlog,
    computed,
    ref,
    useDayjs
} from '#imports'
import type { components as StrapiComponents } from '@core/types/clients/strapi'

const props = defineProps<{
    title: string
    subtitle: string
    searchFieldPlaceholder: string
    errorOptions: {
        noSearchResultText: string
        noBlogsText: string
    }
    posts?: any[]
}>()

const dayjs = useDayjs()
const { getBlogs } = useStrapiBlog()
const { data: fetchedBlogs } = await getBlogs({
    server: true,
    enabled: !props.posts?.length,
})
const blogs = computed(() => props.posts?.length ? props.posts : fetchedBlogs.value)
const searchQuery = ref('')

const date = dayjs.tz().subtract(1, 'year')

function isBlogOlderThanOneYear(blog: StrapiComponents['schemas']['Blog']): boolean {
    if (!blog.publishAt) return false

    return dayjs(blog.publishAt?.publishAt).tz().isBefore(date)
}

function getBlogsByQuery(blog: StrapiComponents['schemas']['Blog'], query: string) {
    return blog
        .title
        .toLowerCase()
        .includes(query.toLowerCase())
}

function filterBlogs(query: string) {
    if (!blogs.value?.length) return []

    return blogs
        .value
        .filter(blog => !isBlogOlderThanOneYear(blog))
        .filter((blog) => {
            return getBlogsByQuery(blog, query)
        })
}

const listOfBlogs = computed((): StrapiComponents['schemas']['Blog'][] => {
    return filterBlogs(searchQuery.value)
})
</script>

<template>
    <section class="blog-section">
        <div class="blog-header-wrapper">
            <div class="blog-section__header">
                <h1 class="blog-section__title">
                    {{ title }}
                </h1>

                <div class="blog-section__subtitle">
                    {{ subtitle }}
                </div>
            </div>
        </div>

        <UIInput
            v-model="searchQuery"
            name="search"
            type="input"
        >
            {{ searchFieldPlaceholder }}
        </UIInput>

        <div v-if="listOfBlogs?.length" class="blog-section__list">
            <NuxtLink
                v-for="blog in listOfBlogs"
                :key="blog.slug"
                :to="{ name: 'blog-slug', params: { slug: blog.slug } }"
                class="blog-section__post"
            >
                <NimbusBlogPost
                    :title="blog.title"
                    :subtitle="blog.subtitle"
                    :label="blog.label"
                    :media-url="blog.media?.formats?.thumbnail?.url || null"
                    :date="blog.publishAt?.publishAt ?? ''"
                />
            </NuxtLink>
        </div>

        <div v-else-if="searchQuery?.length">
            <div>
                {{ errorOptions.noSearchResultText }}
            </div>
        </div>

        <div v-else>
            {{ errorOptions.noBlogsText }}
        </div>
    </section>
</template>

<style lang="scss" scoped>
.blog-section {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &__title {
        font-family: $brand-font-family;
        font-weight: $text-bold;
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    &__post {
        text-decoration: none;
    }
}
</style>
